import React, { useState, useEffect } from 'react';

interface StepProps {
  recordId: number; // ID of the record to edit
  handleClose: () => void; // Close the popup
  onUpdate: () => void; // Callback to refresh the data after update
}

interface FormData {
  campaignName: string;
  availableKeywords: string[];
  selectedKeywords: string[];
  negativeKeywords: string;
  targetingType: string; // RON or Contextual Targeting
  [key: string]: any;
}

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ;

const TargetingStepPopup: React.FC<StepProps> = ({
  recordId,
  handleClose,
  onUpdate,
}) => {
  const [formData, setFormData] = useState<FormData>({
    campaignName: '',
    availableKeywords: [],
    selectedKeywords: [],
    negativeKeywords: '',
    targetingType: 'RON',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!recordId) {
        console.error("No recordId provided");
        return;
      }
      try {

        const baseUrl = window.location.hostname === 'localhost'
        ? `http://localhost:5000/api/targeting/${recordId}`
        : `https://bidvertiser-api.vercel.app/api/targeting/${recordId}`;
  
      // Construct the API URL with the date range query parameters
      const apiUrl = `${baseUrl}`;
  
      // Fetch data from the selected API
      const response = await fetch(apiUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch data: ${response.statusText}`);
        }
        const data = await response.json();
        console.log(data);
        setFormData({
          campaignName: data.campaignName || '',
          availableKeywords: data.availableKeywords || [],
          selectedKeywords: data.selectedKeywords || [],
          negativeKeywords: data.negativeKeywords || '',
          targetingType: data.targetingType || 'RON',
        });
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load campaign data. Please try again.");
      }
    };

    fetchData();
  }, [recordId]);

  useEffect(() => {
    const fetchKeywords = async () => {
      try {
        const data = [
          "Solar", "Solar panel installation", "Best solar companies near me", "Solar energy cost savings",
          "Home solar financing options", "Solar tax credits 2025", "Residential solar systems",
          "Buy solar panels online", "Solar panel efficiency comparison", "Solar leasing vs buying",
          "Solar battery storage solutions", "How much do solar panels cost", "Off-grid solar systems",
          "Best solar panels for homes", "Solar installation incentives", "Solar power for home use",
          "Auto Warranty", "Best extended car warranty", "Car warranty companies near me",
          "Is an extended warranty worth it?", "Bumper-to-bumper warranty cost", "Cheap auto warranty plans",
          "Vehicle protection plans", "Used car extended warranty", "Compare car warranty providers",
          "Auto warranty scams to avoid", "What does an extended warranty cover?",
          "Best car warranty for older vehicles", "New car warranty options",
          "Car repair insurance vs extended warranty", "Auto service contracts explained",
          "Factory vs third-party warranties", "Mortgage", "Best mortgage rates today",
          "Mortgage refinance options", "First-time homebuyer loans", "FHA vs conventional mortgage",
          "Mortgage pre-approval process", "How much mortgage can I afford?",
          "Compare mortgage lenders", "30-year vs 15-year mortgage", "VA home loan benefits",
          "Home equity loan vs HELOC", "Best banks for mortgage loans",
          "Mortgage closing costs explained", "Adjustable-rate mortgage vs fixed",
          "Mortgage calculator with taxes and insurance", "How to get a low mortgage rate",
          "Windows", "Best window replacement companies", "Energy-efficient windows for homes",
          "Window installation cost", "Vinyl vs wood windows", "Double-pane vs triple-pane windows",
          "How much do new windows cost?", "Best brands for home windows", "Window financing options",
          "Cheap window replacements near me", "DIY window installation guide",
          "Sliding vs casement windows", "Hurricane-proof windows cost", "Window insulation for winter",
          "Custom windows for homes", "Best time to replace windows", "Roofing",
          "Best roofing contractors near me", "Roof replacement cost estimator", "Metal roof vs shingles",
          "Affordable roofing solutions", "Best roofing materials for hot climates",
          "How long does a new roof last?", "Roof financing options", "Flat roof vs pitched roof",
          "Emergency roof repair services", "DIY roof repair guide", "Best time of year to replace a roof",
          "Energy-efficient roofing options", "Roofing warranty comparison",
          "How to choose a roofing contractor", "Solar shingles vs solar panels",
          "Home Remodeling", "Best home remodeling companies", "Kitchen remodel cost estimator",
          "Bathroom renovation ideas", "Home renovation financing options", "Cheap home remodeling ideas",
          "DIY vs professional home remodeling", "Basement finishing cost",
          "Best flooring options for homes", "Interior home design trends", "Smart home upgrades 2025",
          "Open concept kitchen remodel", "Cost of adding a room to a house",
          "Home remodeling return on investment", "Whole-house renovation guide",
          "How to hire a home remodeling contractor"
        ];
      
        setFormData((prev) => ({ ...prev, availableKeywords: data }));
      } catch (error) {
        console.error('Error fetching keywords:', error);
      }
    };

    fetchKeywords();
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleKeywordSelection = (keyword: string) => {
    if (formData.selectedKeywords.includes(keyword)) {
      setFormData((prev) => ({
        ...prev,
        selectedKeywords: prev.selectedKeywords.filter((k) => k !== keyword),
        availableKeywords: [...prev.availableKeywords, keyword],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        selectedKeywords: [...prev.selectedKeywords, keyword],
        availableKeywords: prev.availableKeywords.filter((k) => k !== keyword),
      }));
    }
  };

  const handleUpdate = async () => {
    console.log("Updating targeting for recordId:", recordId);
    console.log("Updated formData:", formData);
    if (!recordId) {
      console.error("No record ID provided");
      return;
    }

    setLoading(true);
    setError(null);

    try {

       // Check the current environment to determine the correct API URL
       const baseUrl = window.location.hostname === 'localhost'
       ? `http://localhost:5000/api/targeting/updatingKeywords/${recordId}`
       : `https://bidvertiser-api.vercel.app/api/targeting/updatingKeywords/${recordId}`;
 
     // Construct the API URL with the date range query parameters
     const apiUrl = `${baseUrl}`;


      const response = await fetch(apiUrl, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error(`Failed to update record: ${response.statusText}`);
      }

      alert("Targeting updated successfully!");
      onUpdate(); // Refresh parent data
      handleClose(); // Close the popup
    } catch (err: any) {
      console.error("Error updating targeting:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div style={{ marginLeft: '30px' }}>
      <div style={{ display: 'flex', marginBottom: '10px' }}>
        <p>Campaign: </p>
        <p style={{ fontWeight: '500', marginLeft: '4px' }}>{formData.campaignName}</p>
      </div>

      <div>
        <h4>Targeting Options</h4>
        <label>
          <input
            type="radio"
            name="targetingType"
            value="RON"
            checked={formData.targetingType === 'RON'}
            onChange={handleInputChange}
          />
          Enable RON (Run Of Network) - No Targeting
        </label>
        <p>
          Choose this option if you want your ad to show for every impression served. This option is useful for websites that need high traffic volume at a lower price and for non-targeted products such as general downloads, casinos, general affiliate offers, etc.
        </p>

        <label>
          <input
            type="radio"
            name="targetingType"
            value="Contextual"
            checked={formData.targetingType === 'Contextual'}
            onChange={handleInputChange}
          />
          Enable Contextual Targeting
        </label>
        <p>
          Use this tool to effectively target your ads by keywords and brands.

          Use broad keywords, preferably 1 keyword per phrase (maximum of 2).
          Do not use long-tail keywords. To exclude keywords, use a minus sign (e.g., -keyword).

          Important! Targeting brand names of leading competitors can attract highly engaged visitors. For instance, using the keyword "Adidas" while promoting sports shoes can effectively drive traffic, even for a competing brand.
        </p>

        {formData.targetingType === 'Contextual' && (
          <div style={{ display: 'flex', gap: '20px', marginTop: '20px' }}>
            {/* Available Keywords */}
            <div style={{ width: '300px', fontSize: '12px' }}>
              <h3>Available Keywords</h3>
              <ul
                style={{
                  listStyle: 'none',
                  padding: 0,
                  border: '1px solid #ccc',
                  height: '200px',
                  overflowY: 'scroll',
                }}
              >
                {formData.availableKeywords.map((keyword, index) => (
                  <li
                    key={index}
                    style={{
                      padding: '5px',
                      cursor: 'pointer',
                    }}
                    onDoubleClick={() => handleKeywordSelection(keyword)}
                  >
                    {keyword}
                  </li>
                ))}
              </ul>
            </div>

            {/* Selected Keywords */}
            <div style={{ width: '300px', fontSize: '12px' }}>
              <h3>Selected Keywords</h3>
              <ul
                style={{
                  listStyle: 'none',
                  padding: 0,
                  border: '1px solid #ccc',
                  height: '200px',
                  overflowY: 'scroll',
                }}
              >
                {formData.selectedKeywords.map((keyword, index) => (
                  <li
                    key={index}
                    style={{
                      padding: '5px',
                      cursor: 'pointer',
                    }}
                    onDoubleClick={() => handleKeywordSelection(keyword)}
                  >
                    {keyword}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>

      <button
        style={{ marginTop: '20px', padding: '10px', backgroundColor: '#1A3F66', color: 'white', borderRadius: '2px' }}
        type="button"
        onClick={handleUpdate}
      >
        Update
      </button>

      {error && <div style={{ color: 'red', marginTop: '10px' }}>Error: {error}</div>}
    </div>
  );
};

export default TargetingStepPopup;
